import { useMemo } from 'react';

// New simplified variation types
export type VariationType = 'singular' | 'plural';

// New simplified content variations interface
export interface ContentVariations {
  singular: string;
  plural: string;
}

// Legacy format interface for backward compatibility
export interface LegacyContentVariations {
  original: string;
  tuSingular?: string;
  tuPlural?: string;
  udSingular?: string;
  udPlural?: string;
}

export interface ProjectTreatment {
  clientSize: 'SINGULAR' | 'PLURAL' | string;
  clientTone: 'INFORMAL' | 'FORMAL' | string;
}

export const useContentVariation = (project?: ProjectTreatment) => {
  const variationKey = useMemo((): VariationType => {
    if (!project) return 'singular';

    const { clientSize } = project;

    // Map project settings to variation key (simplified - only based on clientSize)
    return clientSize === 'PLURAL' ? 'plural' : 'singular';
  }, [project]);

  const getVariationText = (content: any): string => {
    if (!content) return '';

    // Handle different content formats
    let variations: ContentVariations | LegacyContentVariations | null = null;

    // PRIORITY 1: Check for NEW format {singular, plural}
    if (typeof content === 'object' && 'singular' in content && 'plural' in content) {
      variations = content as ContentVariations;
    } else if (typeof content === 'object' && content.content && typeof content.content === 'object' &&
               'singular' in content.content && 'plural' in content.content) {
      variations = content.content as ContentVariations;
    }

    // If new format found, return the appropriate variation
    if (variations && 'singular' in variations) {
      const newVariations = variations as ContentVariations;
      const text = newVariations[variationKey];
      if (text && text.trim()) {
        return text;
      }
      // Fallback to singular if plural is empty
      return newVariations.singular || '';
    }

    // PRIORITY 2: Check for LEGACY format {original, tuSingular, tuPlural, udSingular, udPlural}
    if (typeof content === 'object' && content.original !== undefined) {
      variations = content as LegacyContentVariations;
    } else if (typeof content === 'object' && content.content && typeof content.content === 'object' && content.content.original !== undefined) {
      variations = content.content as LegacyContentVariations;
    }

    // If legacy format found, map to new format and return appropriate variation
    if (variations && 'original' in variations) {
      const legacyVariations = variations as LegacyContentVariations;

      // Map legacy format to new format
      const mappedSingular = legacyVariations.tuSingular || legacyVariations.udSingular || legacyVariations.original;
      const mappedPlural = legacyVariations.tuPlural || legacyVariations.udPlural || legacyVariations.original;

      // Return based on variation key
      if (variationKey === 'plural' && mappedPlural && mappedPlural.trim()) {
        return mappedPlural;
      }
      return mappedSingular || '';
    }

    // PRIORITY 3: Check for other legacy formats
    if (typeof content === 'object' && content.content && typeof content.content === 'object' && content.content.text) {
      // Legacy format with text property
      return content.content.text;
    } else if (typeof content === 'object' && content.content && typeof content.content === 'string') {
      // Content is a string
      return content.content;
    } else if (typeof content === 'object' && content.text) {
      // Direct text property
      return content.text;
    } else if (typeof content === 'string') {
      // Direct string
      return content;
    }

    // Additional legacy format checks
    if (typeof content === 'object') {
      // Check for nested content.content.content patterns
      if (content.content && content.content.content && typeof content.content.content === 'string') {
        return content.content.content;
      }

      // Check for questionText in multiple levels
      if (content.questionText && typeof content.questionText === 'string') {
        return content.questionText;
      } else if (content.content && content.content.questionText && typeof content.content.questionText === 'string') {
        return content.content.questionText;
      }

      // Check for different text field names
      if (content.textContent && typeof content.textContent === 'string') {
        return content.textContent;
      }

      // Check for value field
      if (content.value && typeof content.value === 'string') {
        return content.value;
      }

      // Try to find any string value in the object
      for (const key of Object.keys(content)) {
        if (typeof content[key] === 'string' && content[key].trim()) {
          return content[key];
        }
      }
    }

    // Final fallback to any available text
    return content.name || content.title || '';
  };

  return {
    variationKey,
    getVariationText
  };
};